import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../partage/api/client';
import type { Feuille, Maillon } from '../partage/api/types';
import { laBulleLue, ouOuvrirLaFiche, type Arrivee } from './ficheParSonId';
import type { ArriveeSurLaFeuille } from '../partage/navigationDeLaFeuille';
import { cles, PREFIXES } from '../partage/relectures';

/**
 * Ouvrir la fiche d'une bulle par son identifiant, d'où qu'on soit (chantier nettoyage-feuille,
 * ticket 01) : sur place quand on est sur sa feuille ou au-dessus, sinon on va sur une feuille et
 * sa fiche s'y ouvre (`ouOuvrirLaFiche`). Son chemin se prend d'abord dans ce qui est déjà lu ;
 * seulement s'il ne s'y trouve pas, on lit sa feuille (une lecture, comme avant).
 */

/** Les feuilles déjà lues, telles que le cache les garde (sans leurs cartes ni « Ici »). */
export function feuillesLues(client: QueryClient): Feuille[] {
  return client
    .getQueriesData<Feuille>({ queryKey: PREFIXES.feuille })
    .filter(([cle, feuille]) => cle.length === 2 && feuille !== undefined)
    .map(([, feuille]) => feuille as Feuille);
}

/** La feuille ouverte, quand on en a une : son chemin, et comment y ouvrir une fiche. */
export interface FeuilleOuverte {
  chemin: readonly Maillon[];
  ouvrir: (id: string) => void;
}

export interface CommentAller {
  /** Hors de la feuille : chez sa mère (par défaut), ou chez elle. */
  arrivee?: Arrivee;
  /** Son chemin, quand on le sait déjà (la mère d'une fiche ouverte). */
  chemin?: readonly Maillon[];
  /** Juste avant d'aller sur une autre feuille. */
  avantDAller?: () => void;
  /**
   * Remplacer l'adresse d'où l'on part au lieu d'en ajouter une : l'adresse d'une fiche
   * (`/bulle/:id`, le toucher d'une notification) ne reste pas dans l'historique, sinon le retour
   * y ramènerait et rouvrirait la fiche.
   */
  remplacer?: boolean;
}

export function useAllerALaFiche(ici: FeuilleOuverte | null = null) {
  const client = useQueryClient();
  const naviguer = useNavigate();
  return (id: string, { arrivee = 'chez-sa-mere', chemin, avantDAller, remplacer = false }: CommentAller = {}): Promise<void> => {
    const decider = (cheminDeLaBulle: readonly Maillon[] | null) => {
      const ou = ouOuvrirLaFiche(id, cheminDeLaBulle, ici?.chemin ?? null, arrivee);
      if (ou === 'sur-place') {
        ici?.ouvrir(id);
        return;
      }
      avantDAller?.();
      naviguer(`/feuille/${ou.feuille}`, { replace: remplacer, state: { ouvrirLaFiche: id } satisfies ArriveeSurLaFeuille });
    };
    const connu = chemin ?? laBulleLue(id, feuillesLues(client))?.chemin;
    if (connu) {
      decider(connu);
      return Promise.resolve();
    }
    // Pas de réseau, ou elle n'existe plus : on y va, la feuille dira ce qu'il en est.
    return client
      .fetchQuery({ queryKey: cles.feuille(id), queryFn: () => api.feuille(id) })
      .then(
        (feuille) => feuille.chemin,
        () => null
      )
      .then(decider);
  };
}
